import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Icon, Loader } from "@src/components/atoms";
import FeedbackFilters from "./FeedbackFilters";
import AppUtils from "@src/utils/appUtils";
import { useDCContext } from "@src/context/dcContext";
import ManagerHooks from "@src/context/hooks/managerHooks";
import { useAuthContext } from "@src/context/authContext";
import CardContainer from "../CardContainer";
import { useManagerContext } from "../../../context/managerContext";

const FeedbackItem = (props) => {
  const { comment, month, acronym } = props;

  return (
    <div className="flex flex-row mb-3.5">
      <div className="mr-1.5 mt-1.5 flex-grow-0">
        <Icon
          name="feedbackAlt"
          backgroundColor="transparent"
          textColor="primary-800" //#05606C
          size="xl"
          padding={false}
          inline
        />
      </div>
      <div className="bg-white rounded-xl font-medium py-3 px-6 grow">
        <div className="flex flex-row items-center text-primary-800 mb-1">
          <div>{moment.utc(month).format("MMMM YYYY")}</div>
          <div className="bg-primary-300 py-1 px-2 rounded-md ml-3">
            {acronym}
          </div>
        </div>
        <div>{comment}</div>
      </div>
    </div>
  );
};

/** event feedback AKA comments, with various filters */
const FeedbackCard = () => {
  const { profile } = useManagerContext();
  const launchDate = profile.customer.contractStartDate;

  const { getDynamicReplacement } = useDCContext();
  const auth = useAuthContext();
  const role = auth.activeRole;

  //new local states
  const [filters, setFilters] = useState({
    startMonth: null,
    endMonth: null,
    behaviour: "ALL",
    additionalFilters: {
      success: { value: "ALL" },
      origin: { value: "ALL" },
      destination: { value: "ALL" },
      vehicle: [],
    },
  });
  const [params, setParams] = useState({
    startDate: moment.utc(launchDate).startOf("month").toDate(),
    endDate: new Date(),
  });
  const [body, setBody] = useState({});

  const {
    data: { comments } = {},
    isPending: commentsPending,
    error: commentsError,
  } = ManagerHooks.useGetFeedbackSearchResult(
    role,
    params.startDate,
    params.endDate,
    body,
  );
  const {
    data: { vehicleTypes } = {},
    isPending: vehiclesPending,
    error: vehiclesError,
  } = ManagerHooks.useGetVehicles(role);
  const {
    data: { routes } = {},
    isPending: routesPending,
    error: routesError,
  } = ManagerHooks.useGetRoutes(role);

  const [isLoading, setIsLoading] = useState(false);

  const updateResults = async (newFilters) => {
    setIsLoading(true);
    const newParams = {
      startDate: newFilters.startMonth,
      endDate: moment.utc(newFilters.endMonth).endOf("month").valueOf(),
    };
    const body = {};
    if (newFilters.behaviour !== "ALL")
      body.behaviourNames = [newFilters.behaviour];
    if (newFilters.additionalFilters.success.value !== "ALL")
      body.success = newFilters.additionalFilters.success.value === "SUCCESS";
    if (newFilters.additionalFilters.origin.value !== "ALL")
      body.origin = newFilters.additionalFilters.origin.value;
    if (newFilters.additionalFilters.destination.value !== "ALL")
      body.destination = newFilters.additionalFilters.destination.value;
    if (newFilters.additionalFilters.vehicle.length > 0)
      body.vehicles = newFilters.additionalFilters.vehicle.map((v) => ({
        vehicleType: v,
      }));

    setParams(newParams);
    setBody(body);
    setFilters(newFilters);
    setIsLoading(false);
  };

  //dynamic content
  const noResultsText = getDynamicReplacement(
    "fuel_manager__activity__feedback_section__no_results_text",
  );

  const stateFilters = vehiclesPending || routesPending ? "loading" : "visible";
  const stateResults =
    commentsPending || isLoading
      ? "loading"
      : comments?.length > 0
        ? "list"
        : "no-results";

  if (commentsError) {
    console.warn("error getting comments: ", commentsError);
  }
  if (vehiclesError) {
    console.warn("error getting vehicles: ", vehiclesError);
  }
  if (routesError) {
    console.warn("error getting routes: ", routesError);
  }
  return (
    <CardContainer bgColor="neutral-100" allowOverflow>
      {stateFilters === "loading" && <Loader />}
      {stateFilters === "visible" && (
        <FeedbackFilters
          filters={filters}
          vehicleTypes={vehicleTypes}
          setFilters={setFilters}
          updateResults={updateResults}
          routes={routes}
        />
      )}
      {stateResults === "loading" && (
        <div className="p-5 flex flex-col grow">
          <Loader size="xl" />
        </div>
      )}
      {stateResults === "no-results" && (
        <div className="w-full py-5 text-center">{noResultsText}</div>
      )}
      {stateResults === "list" && (
        <div className="px-4 lg:px-8 py-4 mb-5 max-h-[480px] overflow-y-auto">
          {comments.map((result, index) => (
            <FeedbackItem
              comment={result.comment}
              month={new Date(Number(result.eventOccurrenceMonth))}
              acronym={result.behaviourAcronym}
              key={`feedbackItem${index}`}
            />
          ))}
        </div>
      )}
    </CardContainer>
  );
};

export const feedbackCardControls = {};
FeedbackCard.propTypes = AppUtils.getPropTypeFromControls(feedbackCardControls);

export default FeedbackCard;
